<template>
    <div
        class="sentence-control"
        :class="{ 'sentence-control--reviewer-embed': isProofreadingReviewer }"
        @click.stop
    >
        <div class="sentence-control__inner">
            <div v-if="showControl('play')" class="sentence-control__play">
                <button
                    type="button"
                    class="button button--blank sentence-control__play-button"
                    :class="{ 'sentence-control__play-button--pill': isProofreadingReviewer }"
                    @click="playFromPart"
                >
                    <IconPlayOutline v-if="isProofreadingReviewer" stroke="#fff" />
                    <svg
                        v-else
                        width="14"
                        height="18"
                        viewBox="0 0 20 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M18.9189 10.2996L3.21078 0.237184C2.7464 -0.0439058 2.16977 0.00292533 1.73427 0.00292533C-0.00772719 0.00292533 5.97665e-07 1.37033 5.97665e-07 1.71676V22.2832C5.97665e-07 22.576 -0.00762557 23.9971 1.73427 23.9971C2.16977 23.9971 2.7465 24.0437 3.21078 23.7627L18.9188 13.7005C20.2081 12.9205 19.9853 12 19.9853 12C19.9853 12 20.2082 11.0795 18.9189 10.2996Z"
                            fill="#A6A7BE"
                        />
                    </svg>

                    <span>
                        {{ $t('textEntityControl.play') }}
                    </span>
                </button>
            </div>

            <div v-if="showPersonages" class="sentence-control__controls">
                <PersonagesSelect
                    :speaker-slug="part.speaker.slug"
                    @selected="onPersonageSelected"
                />
            </div>
            <div
                v-if="showCorrections"
                class="sentence-control__controls"
                :disabeld="!showCorrections"
                :class="{
                    'sentence-control__controls--disabled':
                        !showCorrections || selector.isInRange(part.id),
                }"
                title="Create Corrections"
            >
                <CorrectionsAdd
                    :book-id="book.id"
                    :part="part"
                    :trigger-class="correctionsTriggerClass"
                    @add="updateWithRefetch(part)"
                >
                    <template v-if="isReviewer" #trigger>
                        <template v-if="isProofreadingReviewer">
                            <IconMessageSquarePlus />
                            <span>{{ $t('textEntityControl.leaveComment') }}</span>
                        </template>
                        <span v-else style="line-height: 18px">
                            {{ $t('textEntityControl.addCorrection') }}
                        </span>
                    </template>
                </CorrectionsAdd>
            </div>
            <div
                v-if="!part.immutable && showControl('split')"
                class="sentence-control__controls"
                :class="{
                    'sentence-control__controls--disabled': selector.isInRange(part.id),
                }"
                title="Split Sentence"
            >
                <SentenceSplitter
                    :part="part"
                    @split="fetchInitialBookparts({ page: $route.query.page })"
                />
            </div>
            <div
                v-if="!part.immutable && showControl('merge')"
                :title="
                    !selector.isInRange(part.id)
                        ? 'Select 2 consecutive sentences to glue them together'
                        : ''
                "
                class="sentence-control__controls"
                :class="{
                    'sentence-control__controls--disabled':
                        !selector.isInRange(part.id) || selector.selection.length > 2,
                }"
            >
                <SentenceMerger @merge="fetchInitialBookparts({ page: $route.query.page })" />
            </div>
            <div v-if="!part.immutable && showControl('delete')" class="sentence-control__controls">
                <SentenceDeleter
                    :bookpart="part"
                    @delete="
                        fetchInitialBookparts({ page: $route.query.page, preserveScroll: true })
                    "
                />
            </div>
        </div>
        <div class="sentence-control__inner">
            <div
                v-if="!part.immutable && showControl('type')"
                class="sentence-control__controls"
                :class="{
                    'sentence-control__controls--disabled': selector.isInRange(part.id),
                }"
            >
                <div class="sentence-control__controls-label">
                    {{ $t('textEntityControl.textType') }}
                </div>
                <TypeTextChanger
                    :bookpart="part"
                    :loading="loading"
                    @change="fetchInitialBookparts({ page: $route.query.page })"
                />
            </div>
            <div
                v-if="!isReviewer"
                class="sentence-control__controls sentence-control__controls--sample"
                :class="{
                    'sentence-control__controls--disabled': selector.isInRange(part.id),
                }"
            >
                <Checkbox
                    :model-value="part.is_sample_flag"
                    :label="$t('editor.toolbar.isSample')"
                    @update:model-value="onChangeSample"
                />

                <router-link
                    :to="`/${isEmbed ? 'embed' : 'editor'}/${book.id}/samples`"
                    class="button button--blank"
                >
                    All
                </router-link>
                <button
                    v-if="showCurrentTiming"
                    type="button"
                    class="button button--blank sentence-control__controls-timing"
                    @click="getCurrentTimming"
                    v-text="formattedCurrentTiming"
                />
            </div>
        </div>
        <Spinner v-if="loading">
            <span v-text="generationProgress" />
        </Spinner>
        <button class="sentence-control__close" @click="close()">
            <IconCross />
        </button>
    </div>
</template>

<script>
import Popper from 'popper.js';
import TypeTextChanger from './TypeTextChanger';
import PersonagesSelect from '../Personages/PersonagesSelect';

import SentenceSplitter from '../SentenceSplitter/SentenceSplitter';
import SentenceMerger from '../SentenceMerger/SentenceMerger';
import SentenceDeleter from '../SentenceDeleter/SentenceDeleter';
import CorrectionsAdd from '../CorrectionsAdd/CorrectionsAdd';

import { saveEditProgress } from '../../services/editProgress';
import { mapState, mapGetters, mapActions } from 'vuex';

import { updateAndGenerate } from '../../services/generation';

import { updateBookpart } from '../../api/bookpart';
import { changeBookPartsSpeaker } from '../../api/bookparts';

import { getCurrentTiming } from '../../api/books';

import { Checkbox } from '@speechki-frontend/shared/ui/forms';
import {
    IconCross,
    IconPlayOutline,
    IconMessageSquarePlus,
} from '@speechki-frontend/shared/ui/icons';

export default {
    name: 'SentenceControl',
    components: {
        PersonagesSelect,
        TypeTextChanger,
        SentenceDeleter,
        SentenceSplitter,
        SentenceMerger,
        CorrectionsAdd,
        Checkbox,
        IconCross,
        IconPlayOutline,
        IconMessageSquarePlus,
    },
    inject: ['selector', 'isEmbed', 'registerWsHandler', 'removeWsHandler'],
    props: {
        part: {
            type: Object,
            required: true,
        },
        config: {
            type: Object,
            required: true,
        },
        selection: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            loading: false,
            popper: undefined,
            currentTiming: undefined,
            bookPartUpdatedCount: 0,
        };
    },
    computed: {
        ...mapState({
            types: (state) => state.settings.textTypes,
            book: (state) => state.book.book,
            user: (state) => state.auth.user,
        }),

        //
        ...mapGetters(['isReviewer', 'isAdmin', 'isGuestAccess']),
        ...mapGetters('guestAccess', ['guestAccessActionPermission']),

        isProofreadingReviewer() {
            return this.isEmbed && this.isReviewer;
        },

        correctionsTriggerClass() {
            if (this.isProofreadingReviewer) {
                return 'sentence-control__play-button sentence-control__play-button--secondary';
            }

            return this.isReviewer ? 'sentence-control__play-button' : '';
        },

        showCorrections() {
            if (this.isGuestAccess) {
                return this.guestAccessActionPermission('correction', 'create');
            }

            return (
                this.showControl('corrections') &&
                ((this.book && this.book.reviewers && this.book.reviewers.includes(this.user.pk)) ||
                    this.isAdmin)
            );
        },

        showPersonages() {
            if (!this.showControl('personage')) return false;

            if (this.isGuestAccess) {
                return this.guestAccessActionPermission('personage', 'list');
            }

            return !this.isReviewer;
        },

        showCurrentTiming() {
            return !this.isEmbed || this.guestAccessActionPermission('book', 'current_timing');
        },

        formattedCurrentTiming() {
            if (!this.currentTiming) return 'HH:MM:SS';
            return new Date(this.currentTiming * 1000).toISOString().slice(11, 19);
        },

        generationProgress() {
            return `${this.bookPartUpdatedCount}/${this.selection.length}`;
        },
    },

    watch: {
        bookPartUpdatedCount(val) {
            if (val === this.selection.length) {
                this.loading = false;
            }
        },
    },
    created() {
        saveEditProgress({
            bookId: this.$route.params.id,
            part: this.part.id,
            page: this.$route.query.page,
        });
    },

    mounted() {
        this.setUpPopper();
    },

    beforeUnmount() {
        if (this.popper) {
            // popper.destroy() wipes the inline positioning styles, but the
            // fade-out transition keeps the element in the DOM a bit longer —
            // restore the styles so the popup fades away in place instead of
            // jumping to its static position
            const { cssText } = this.$el.style;
            this.popper.destroy();
            this.$el.style.cssText = cssText;
            this.popper = null;
        }
    },

    methods: {
        ...mapActions('pagedBookparts', ['fetchInitialBookparts', 'updateWithRefetch']),
        ...mapActions('pushToast'),

        setUpPopper() {
            if (this.popper === undefined) {
                // Anchor to the zero-width .sentence__anchor marker so the popup
                // sits above the start of the sentence; Popper v1 can't do this
                // with the inline .sentence__wrapper itself, because on multi-line
                // sentences it measures the union rect of all line fragments.
                // positionFixed keeps coordinates viewport-based — browsers resolve
                // an absolutely positioned child of an inline element against its
                // first line fragment, which drifted the popup outside the editor.
                // Resolved through the DOM: in Vue 3 $parent is the wrapping
                // <transition> instance whose $el is this component itself.
                const wrapper = this.$el.closest('.sentence__wrapper') || this.$el.parentElement;
                const anchor =
                    (wrapper && wrapper.querySelector('.sentence__anchor')) || wrapper || this.$el;

                this.popper = new Popper(anchor, this.$el, {
                    placement: 'top-start',
                    positionFixed: true,
                    eventsEnabled: true,
                    modifiers: {
                        offset: {
                            enabled: true,
                            offset: '0, 10',
                        },
                    },
                });
            } else {
                this.popper.scheduleUpdate();
            }
        },

        playFromPart() {
            this.$emit('playPart', { id: this.part.id });
        },

        async onPersonageSelected({ speaker }) {
            try {
                this.loading = true;

                if (this.selection.length > 1) {
                    await changeBookPartsSpeaker(this.book.id, {
                        speaker: speaker.id,
                        start: this.selection[0].bookpart.id,
                        end: this.selection.at(-1).bookpart.id,
                    });
                    this.registerWsHandler(
                        'record_process_book_part_update',
                        this.onRecordProcessBookPartUpdate
                    );
                } else {
                    if (!this.part.permissions.generate && this.isEmbed) {
                        this.$store.dispatch('pushToast', {
                            type: 'error',
                            title: `Text can't be edited`,
                            text: `You’ve reached the editing limit in this project. <br> To read more about how editing limits in projects work, please follow <a class='link' href='https://speechki.org/faq' target='_blank'>this link</a>`,
                            theme: 'big',
                        });
                        this.loading = false;
                        return;
                    }

                    const data = {
                        speaker: speaker.id,
                    };

                    await updateAndGenerate(this.part.id, data);
                    this.fetchInitialBookparts({ page: this.$route.query.page });
                }
            } catch (err) {
                console.log(err);
            } finally {
                this.loading = !(this.bookPartUpdatedCount === this.selection.length);
                this.bookPartUpdatedCount = 0;
            }
        },

        async onChangeSample(event) {
            try {
                this.loading = true;

                await updateBookpart(this.part.id, {
                    is_sample_flag: event,
                });

                await this.updateWithRefetch(this.part);
            } catch (err) {
                console.log(err);
            } finally {
                this.loading = false;
            }
        },

        showControl(control) {
            return this.config.controls.includes(control);
        },

        async getCurrentTimming() {
            this.loading = true;
            try {
                const res = await getCurrentTiming(this.book.id, {
                    book_part: this.part.id,
                });
                this.currentTiming = res.current_timing;
            } catch (err) {
                console.log(err);
            } finally {
                this.loading = false;
            }
        },

        onRecordProcessBookPartUpdate({ code, reason, errors }) {
            if (code === 200 && reason === 'record_process_book_part_update') {
                if (errors) {
                    this.pushToast({
                        type: 'error',
                        title: this.$t('misc.error'),
                    });
                } else {
                    this.bookPartUpdatedCount++;

                    if (this.bookPartUpdatedCount === this.selection.length) {
                        this.removeWsHandler(
                            'record_process_book_part_update',
                            this.onRecordProcessBookPartUpdate
                        );
                        this.bookPartUpdatedCount = 0;
                        this.loading = false;
                        this.fetchInitialBookparts({
                            page: this.$route.query.page,
                            preserveScroll: true,
                        });
                    }
                }
            }
        },

        close() {
            this.selector.closeSentenceControl();
        },
    },
};
</script>

<style lang="scss" src="./SentenceControl.scss"></style>
